import { Grid } from '@mui/material';
import { Button, DataTable, Modal, Typography } from 'design_system/src';
import {
	FinancialFormTypeEnum,
	RevivePreOfferProfileValues,
} from 'models/financial-form/enums/financial-form.enums';
import { getLatestPreOffer } from 'models/financial-form/helpers/preOffer.helpers';
import { LoanFinancialFormWithExtraFields } from 'models/financial-form/interfaces/financial-form.interfaces';
import { getActiveCreditPolicy } from 'models/loan-offer/controllers/loan-offer.controller';
import {
	formatMopRange,
	formatOutcomeRange,
	formatOverdueBalance,
} from 'models/loan-offer/helpers/credit-policy.helpers';
import { CreditPolicy } from 'models/loan-offer/interfaces/credit-policy.interface';
import { formatDate } from 'models/shared/helpers/dates';
import { priceFormatFinancial } from 'models/shared/helpers/prices';
import { useContext, useEffect, useState } from 'react';
import { ozonerContext } from '../OzonerContext';

const PRE_OFFER_STATUS_LABEL: Record<string, string> = {
	approved: 'Aprobada',
	rejected: 'Rechazada',
	expired: 'Expirada',
};

const PRE_OFFER_STATUS_COLOR: Record<string, string> = {
	approved: 'green_300',
	rejected: 'red_300',
	expired: 'yellow_100',
};

const PRE_OFFER_REJECTION_STATUS_LABEL: Record<string, string> = {
	InvalidTier: 'Rechazo por tier',
	InvalidParams: 'Parametros inválidos',
	NoViableOffers: 'Sin ofertas viables',
};

export const PROFILE_TIER_LABEL: Record<string, string> = {
	B1: 'B1',
	B2: 'B2',
	B3: 'B3',
	NB: 'NB (sin buró)',
	HARD_REJECT: 'Rechazo',
	FRAUD: 'Fraude',
};

export const PROFILE_TIER_COLOR: Record<string, string> = {
	B1: 'green_300',
	B2: 'green_300',
	B3: 'green_300',
	NB: 'yellow_100',
	HARD_REJECT: 'red_300',
	FRAUD: 'red_300',
};

function RiskProfileOzoner() {
	const { financialForm } = useContext(ozonerContext);
	const [policy, setPolicy] = useState<CreditPolicy | null>(null);
	const [showCirculoJson, setShowCirculoJson] = useState(false);

	useEffect(() => {
		getActiveCreditPolicy()
			.then(setPolicy)
			.catch(() => {});
	}, []);

	if (financialForm.form.type !== FinancialFormTypeEnum.loan) return null;

	const form = financialForm.form as LoanFinancialFormWithExtraFields;
	const preOffer = getLatestPreOffer(form.loanApplication?.preOffers);

	if (!preOffer) {
		return (
			<div className="flex_center p_xl">
				<Typography scale="medium" weight="400" textColor="neutral_600">
					No hay información de Risk Profile disponible.
				</Typography>
			</div>
		);
	}

	const statusLabel =
		PRE_OFFER_STATUS_LABEL[preOffer.status] ?? preOffer.status;
	const statusColor = PRE_OFFER_STATUS_COLOR[preOffer.status] ?? 'neutral_600';

	const hasOfferTerms =
		preOffer.status === 'approved' || preOffer.status === 'expired';

	const preOfferData = [
		{ label: 'Versión de política', value: preOffer.policyVersion },
		...(hasOfferTerms
			? [
					{
						label: 'Fecha de expiración',
						value: preOffer.expirationDate
							? formatDate(preOffer.expirationDate)
							: '-',
						valueColor: preOffer.status === 'expired' ? 'red_300' : undefined,
					},
					{
						label: 'Precio máximo de ticket',
						value: priceFormatFinancial(preOffer.maxTicketPrice ?? 0, 0),
						valueColor: 'green_300',
					},
					{
						label: 'Duración máxima (semanas)',
						value: `${preOffer.maxDuration ?? '--'}`,
						valueColor: 'green_300',
					},
					{
						label: '% Enganche mínimo',
						value: `${((preOffer.minDownPaymentPct ?? 0) * 100).toFixed(0)}%`,
						valueColor: 'green_300',
					},
					{
						label: 'Enganche mínimo',
						value: priceFormatFinancial(preOffer.minDownPayment ?? 0, 0),
					},
					{
						label: 'Monto máximo financiable',
						value: priceFormatFinancial(preOffer.maxFinancingAmount ?? 0, 0),
					},
					{
						label: 'Capacidad de financiamiento máxima',
						value: priceFormatFinancial(preOffer.maxFinancingCapacity ?? 0, 0),
						valueColor: 'green_300',
					},
					{
						label: 'Capacidad de pago máxima por la totalidad del crédito',
						value: priceFormatFinancial(preOffer.maxPaymentCapacity ?? 0, 0),
					},
					{
						label: 'Capacidad mensual máxima',
						value: priceFormatFinancial(preOffer.maxMonthlyCapacity ?? 0, 0),
					},
					{
						label: 'Capacidad semanal máxima',
						value: priceFormatFinancial(preOffer.maxPeriodicCapacity ?? 0, 0),
					},
					{
						label: 'Pago semanal',
						value: priceFormatFinancial(preOffer.periodicPayment ?? 0, 0),
						valueColor: 'green_300',
					},
					{
						label: 'Tasa semanal',
						value: `${((preOffer.periodicRateWithTax ?? 0) * 100).toFixed(2)}%`,
					},
					{
						label: 'Tasa semanal con IVA',
						value: `${((preOffer.periodicRateWithTax ?? 0) * 100).toFixed(2)}%`,
					},
					{
						label: 'Pago semanal base',
						value: priceFormatFinancial(preOffer.periodicBasePayment ?? 0, 0),
					},
					{
						label: 'Pago semanal extra (seguro + GPS)',
						value: priceFormatFinancial(preOffer.periodicExtraPayment ?? 0, 0),
					},
					{
						label: 'Monto total de seguro',
						value: priceFormatFinancial(preOffer.totalInsuranceAmount ?? 0, 0),
					},
					{
						label: 'Monto total de GPS',
						value: priceFormatFinancial(preOffer.totalGpsAmount ?? 0, 0),
					},
					{
						label: 'Monto total extra (seguro + GPS)',
						value: priceFormatFinancial(preOffer.totalExtraAmount ?? 0, 0),
					},
					{
						label: 'Ingreso mensual',
						value: priceFormatFinancial(preOffer.monthlyIncome ?? 0, 0),
					},
					{ label: 'Moneda', value: preOffer.currency ?? '--' },
					{
						label: 'Frecuencia de pago',
						value: preOffer.paymentFrequency ?? '--',
					},
					{
						label: 'Uso comercial',
						value: preOffer.commercialUse ? 'Sí' : 'No',
					},
			  ]
			: [
					{
						label: 'Razón de rechazo',
						value: preOffer.reason
							? PRE_OFFER_REJECTION_STATUS_LABEL[preOffer.reason]
							: '-',
						valueColor: 'red_300',
					},
			  ]),
	];

	const circuloCredito = form.loanApplication?.circuloCredito;

	const overdueBalanceOk =
		circuloCredito?.overdueBalance !== undefined &&
		circuloCredito.overdueBalance < 7000;

	const currentMopAndOverdueInstallmentsOk =
		circuloCredito?.currentMop !== undefined &&
		circuloCredito?.overdueInstallments !== undefined &&
		circuloCredito.currentMop < 4 &&
		circuloCredito.overdueInstallments < 3;

	const delinquentAccountsPct =
		circuloCredito?.activeAccountsCount !== undefined &&
		circuloCredito.activeAccountsCount > 0
			? (circuloCredito.delinquentAccountsCount ?? 0) /
			  circuloCredito.activeAccountsCount
			: undefined;

	const passesLaneA =
		circuloCredito?.ficoScore !== undefined &&
		delinquentAccountsPct !== undefined &&
		circuloCredito.ficoScore >= 500 &&
		delinquentAccountsPct < 0.5;

	const passesLaneB =
		delinquentAccountsPct !== undefined &&
		circuloCredito?.delinquentAccountsCount !== undefined &&
		delinquentAccountsPct < 0.3 &&
		circuloCredito.delinquentAccountsCount < 2;

	const circuloData = [
		{
			label: 'Gasto mensual verificado',
			value:
				circuloCredito?.bureauMonthlyExpenses !== undefined
					? priceFormatFinancial(circuloCredito.bureauMonthlyExpenses, 0)
					: '--',
		},
		{
			label: 'Saldo vencido',
			value:
				circuloCredito?.overdueBalance !== undefined
					? priceFormatFinancial(circuloCredito.overdueBalance, 0)
					: '--',
			valueColor: overdueBalanceOk ? 'green_300' : undefined,
		},
		{
			label: 'Mop actual',
			value: circuloCredito?.currentMop?.toString() ?? '--',
			valueColor: currentMopAndOverdueInstallmentsOk ? 'green_300' : undefined,
		},
		{
			label: 'Cuotas vencidas',
			value: circuloCredito?.overdueInstallments?.toString() ?? '--',
			valueColor: currentMopAndOverdueInstallmentsOk ? 'green_300' : undefined,
		},
		{
			label: 'Fico Score',
			value: circuloCredito?.ficoScore?.toString() ?? '--',
			valueColor: passesLaneA ? 'green_300' : undefined,
		},
		{
			label: 'Cuentas activas',
			value: circuloCredito?.activeAccountsCount?.toString() ?? '--',
		},
		{
			label: 'Cuentas con mora',
			value: circuloCredito?.delinquentAccountsCount?.toString() ?? '--',
			valueColor: passesLaneB ? 'green_300' : undefined,
		},
		{
			label: '% Cuentas con mora en activas',
			value: circuloCredito?.activeAccountsCount
				? `${(
						((circuloCredito.delinquentAccountsCount ?? 0) /
							circuloCredito.activeAccountsCount) *
						100
				  ).toFixed(0)}%`
				: '--',
			valueColor: passesLaneA || passesLaneB ? 'green_300' : undefined,
		},
		{
			label: 'Mop histórico',
			value: circuloCredito?.mop?.toString() ?? '--',
		},
	];

	const profileTierLabel =
		PROFILE_TIER_LABEL[preOffer.scoring?.profileTier ?? ''] ??
		preOffer.scoring?.profileTier ??
		'--';
	const profileTierColor =
		PROFILE_TIER_COLOR[preOffer.scoring?.profileTier ?? ''] ?? 'neutral_600';

	const { revivedUnderProfile } = preOffer;

	const scoringData = [
		{
			label: 'Tier de perfil',
			value: profileTierLabel,
			valueColor: profileTierColor,
		},
		{
			label: 'Aprobado por tier',
			value: preOffer.scoring?.approvedByTier ? 'Sí' : 'No',
			valueColor: preOffer.scoring?.approvedByTier ? 'green_300' : 'red_300',
		},
		...(preOffer.scoring?.whitelist
			? [
					{
						label: 'Lista blanca',
						value: preOffer.scoring.whitelist,
						valueColor: 'green_300',
					},
			  ]
			: []),
		...(revivedUnderProfile
			? [
					{
						label: 'Preoferta regenerada bajo',
						value: RevivePreOfferProfileValues[revivedUnderProfile],
						valueColor: 'yellow_100',
					},
			  ]
			: []),
	];

	const approvedTierRows =
		policy?.eligibilityRows.filter((row) => row.approved && row.mop) ?? [];
	const noBureauRow = policy?.eligibilityRows.find(
		(row) => row.approved && !row.mop,
	);
	const rejectedTierRows =
		policy?.eligibilityRows.filter((row) => !row.approved) ?? [];

	const tierReferenceRows = [
		...approvedTierRows,
		...(noBureauRow ? [noBureauRow] : []),
		...rejectedTierRows,
	];

	return (
		<Grid container direction="row" spacing={3} className="h_100_per p_t_md">
			<Grid item xs={6}>
				<div className="bg_neutral_0 border_neutral_400 border_solid border_1 br_sm overflow_hidden h_100_per">
					<div className="p_md display_flex flex_justify_between flex_align_center border_b_1 border_solid border_neutral_300">
						<Typography scale="large" weight="600" textColor="neutral_900">
							Pre-Oferta
						</Typography>
						<Typography
							scale="small"
							weight="600"
							textColor="neutral_0"
							className={`br_xs p_x_md p_y_xxxs bg_${statusColor}_nocontrast`}
						>
							{statusLabel}
						</Typography>
					</div>
					<div className="p_md">
						<DataTable data={preOfferData} weightLabels={2} weightValues={2} />
					</div>
				</div>
			</Grid>
			<Grid item xs={6}>
				{circuloCredito && (
					<div className="bg_neutral_0 border_neutral_400 border_solid border_1 br_sm overflow_hidden m_b_md">
						<div className="p_md display_flex flex_justify_between flex_align_center border_b_1 border_solid border_neutral_300">
							<Typography scale="large" weight="600" textColor="neutral_900">
								Círculo de Crédito
							</Typography>
							{circuloCredito?.responseData && (
								<Button
									variant="link"
									scale="small"
									onClick={() => setShowCirculoJson(true)}
								>
									Ver JSON
								</Button>
							)}
						</div>
						<div className="p_md">
							<DataTable data={circuloData} weightLabels={2} weightValues={2} />
						</div>
					</div>
				)}
				<div className="bg_neutral_0 border_neutral_400 border_solid border_1 br_sm overflow_hidden">
					<div className="p_md border_b_1 border_solid border_neutral_300">
						<Typography scale="large" weight="600" textColor="neutral_900">
							Scoring
						</Typography>
					</div>
					<div className="p_md">
						<DataTable data={scoringData} weightLabels={2} weightValues={2} />
					</div>
					{policy && (
						<div className="p_md border_t_1 border_solid border_neutral_300">
							<Typography
								scale="small"
								weight="600"
								textColor="neutral_700"
								className="m_b_xs"
							>
								Referencia de tiers ({policy.policyVersion})
							</Typography>
							<table
								style={{
									width: '100%',
									borderCollapse: 'collapse',
									fontSize: '12px',
								}}
							>
								<thead>
									<tr className="bg_neutral_200">
										<th
											style={{
												textAlign: 'left',
												padding: '6px 8px',
												fontWeight: 600,
											}}
										>
											Perfil
										</th>
										<th
											style={{
												textAlign: 'left',
												padding: '6px 8px',
												fontWeight: 600,
											}}
										>
											Rango
										</th>
										<th
											style={{
												textAlign: 'center',
												padding: '6px 8px',
												fontWeight: 600,
											}}
										>
											Estado
										</th>
									</tr>
								</thead>
								<tbody>
									{tierReferenceRows.map((row) => (
										<tr
											key={row.id}
											className="border_b_1 border_solid border_neutral_200"
										>
											<td
												style={{ padding: '6px 8px', fontWeight: 600 }}
												className={
													row.approved ? 'text_green_300' : 'text_red_300'
												}
											>
												{PROFILE_TIER_LABEL[row.profileCode] ?? row.profileCode}
											</td>
											<td
												style={{ padding: '6px 8px' }}
												className="text_neutral_700"
											>
												{formatMopRange(row.mop)}
												{formatOverdueBalance(row.overdueBalance)}
												{formatOutcomeRange(row.outcome)}
											</td>
											<td style={{ padding: '6px 8px', textAlign: 'center' }}>
												<span
													className={`br_xs p_x_xs text_neutral_0 bg_${
														row.approved ? 'green_300' : 'red_300'
													}_nocontrast`}
													style={{ fontSize: 11 }}
												>
													{row.approved ? 'Aprobado' : 'Rechazado'}
												</span>
											</td>
										</tr>
									))}
								</tbody>
							</table>
							<Typography
								scale="small"
								weight="400"
								textColor="neutral_600"
								className="m_t_xs"
							>
								MOP: Calificación según los días de atraso. MOP mayor a 3 son 60
								días o más de atraso.
							</Typography>
							<Typography
								scale="small"
								weight="400"
								textColor="neutral_600"
								className="m_t_xs"
							>
								CPM: Compromiso de pagos mensual calculado desde Círculo de
								Crédito
							</Typography>
							<Typography
								scale="small"
								weight="400"
								textColor="neutral_600"
								className="m_t_xs"
							>
								SV: Saldo vencido de sus cuentas activas
							</Typography>
							<Typography
								scale="small"
								weight="400"
								textColor="neutral_600"
								className="m_t_xs"
							>
								Nota: Si el SV es menor a $ 2.000 se aprueba (B2 o B3) aunque el
								el MOP histórico sea mayor a 3.
							</Typography>
						</div>
					)}
				</div>
			</Grid>
			<Modal open={showCirculoJson} setOpen={setShowCirculoJson}>
				<Typography scale="large" weight="600" className="m_b_md">
					Círculo de Crédito — responseData
				</Typography>
				<pre
					style={{
						maxHeight: '70vh',
						overflow: 'auto',
						fontSize: 12,
						whiteSpace: 'pre-wrap',
						wordBreak: 'break-word',
					}}
				>
					{JSON.stringify(circuloCredito?.responseData, null, 2)}
				</pre>
			</Modal>
		</Grid>
	);
}

export default RiskProfileOzoner;
